import { elementsDansLOrdre, grouperEnArbre, tacheCreation } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { api, corps, ecrit, lire, requeteMoi, requetes, type Tache, type VueTaches } from '../api'
import { retenirFile } from '../composants/FileParcours'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { Icone, type NomIcone } from '../composants/Icone'
import { CarteTache, ICONES_STATUT_TACHE, LigneTache } from '../composants/LigneTache'
import { BasculeRegroupement, ListeGroupee, useRegroupement } from '../composants/ListeGroupee'
import { Vide } from '../composants/Section'
import { EntetePage, Onglets } from '../composants/Ui'
import { LIBELLES_STATUT_TACHE } from '../libelles'

const VUES: { vue: VueTaches; libelle: string; aide: string }[] = [
  {
    vue: 'ma_semaine',
    libelle: 'Ma semaine',
    aide: 'Ce qui vous revient : en retard, sans date, dû dans les sept jours, ou confié à vous et pas encore ouvert (« Nouvelle »). Une tâche « à prendre » est chez tous ses garants jusqu’à ce que l’un d’eux la prenne. Ce qui est chez un tiers se suit dans « En attente de tiers » et ne revient ici qu’en retard.',
  },
  {
    vue: 'tableau',
    libelle: 'Tableau',
    aide: 'Toutes les tâches ouvertes, par statut, et celles terminées ces quinze derniers jours.',
  },
  {
    vue: 'en_attente_tiers',
    libelle: 'En attente de tiers',
    aide: 'Ce qui est chez un tiers : en attente de sa réponse, ou confié à lui. Les plus anciennes d’abord, avec l’âge de l’attente.',
  },
  {
    vue: 'reportees',
    libelle: 'Reportées',
    aide: 'Mises de côté jusqu’à un jour, ou jusqu’à la préparation du bilan : absentes des autres listes jusque-là, elles reviennent « Nouvelle ». Les plus proches du retour d’abord.',
  },
]

const COLONNES = ['a_faire', 'en_cours', 'en_attente_tiers', 'faite'] as const

export function FormulaireTache({
  fermer,
  echeanceId,
}: {
  fermer: () => void
  echeanceId?: string
}) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites)
  const ecrivables = (entites.data ?? []).filter((e) => !e.archivee && moi && ecrit(moi, e.id))
  const champs: Champ[] = [
    { nom: 'titre', libelle: 'Titre', type: 'texte', requis: true },
    ...(echeanceId
      ? []
      : [
          {
            nom: 'entiteId',
            libelle: 'Entité',
            type: 'choix' as const,
            requis: !moi?.administrateur,
            aide: moi?.administrateur
              ? 'Sans entité : visible des seuls administrateurs.'
              : undefined,
            options: ecrivables.map((e) => ({ valeur: e.id, libelle: e.nom })),
          },
        ]),
    { nom: 'dateDue', libelle: 'Pour le', type: 'date' },
    { nom: 'urgente', libelle: 'Urgente', type: 'booleen' },
    { nom: 'description', libelle: 'Précisions', type: 'texte' },
  ]
  return (
    <Formulaire
      champs={champs}
      libelleBouton="Créer la tâche"
      onAnnuler={fermer}
      onValider={async (v) => {
        await lire(
          api.taches.$post({
            json: corps(tacheCreation, { ...v, ...(echeanceId && { echeanceId }) }),
          }),
        )
        await client.invalidateQueries()
        fermer()
      }}
    />
  )
}

const ICONES_VUE: Record<(typeof VUES)[number]['vue'], NomIcone> = {
  ma_semaine: 'person',
  tableau: 'view_kanban',
  en_attente_tiers: 'hourglass_top',
  reportees: 'snooze',
  toutes: 'view_list',
}

function parColonne(taches: Tache[]): Tache[] {
  return COLONNES.flatMap((statut) => taches.filter((t) => t.statut === statut))
}

/** En-tête d'un groupe : ses tâches confiées à qui regarde et pas encore ouvertes. */
const MARQUE_NOUVELLES = {
  est: (t: Tache) => t.nouvelle,
  libelle: (n: number) => (n > 1 ? `${n} nouvelles` : 'Nouvelle'),
}

/** Tiers puis sorte ; sans tiers, à la fin. */
function arbreTaches(taches: Tache[]) {
  return grouperEnArbre(
    taches,
    [(t) => t.regroupement.tiers, (t) => t.regroupement.sorte],
    'Sans tiers',
  )
}

function Tableau({ taches }: { taches: Tache[] }) {
  return (
    <div className="tableau-taches">
      {COLONNES.map((statut) => {
        const liste = taches.filter((t) => t.statut === statut)
        return (
          <section key={statut} className="colonne-taches">
            <h3>
              <Icone nom={ICONES_STATUT_TACHE[statut]} />
              {LIBELLES_STATUT_TACHE[statut]}
              <span className="mono">{liste.length}</span>
            </h3>
            {liste.length === 0 && <Vide>Rien.</Vide>}
            {liste.map((t) => (
              <CarteTache key={t.id} t={t} vue="tableau" />
            ))}
          </section>
        )
      })}
    </div>
  )
}

export function Taches({
  vue,
  pour,
  onChangeVue,
  onChangePour,
}: {
  vue: VueTaches
  /** Pour qui : absent, le défaut de chaque vue (soi pour « Ma semaine », tout le monde ailleurs). */
  pour: string | undefined
  onChangeVue: (vue: VueTaches) => void
  onChangePour: (pour: string) => void
}) {
  const [ajout, setAjout] = useState(false)
  const moi = useQuery(requeteMoi).data
  const personnes = useQuery(requetes.personnes).data
  const avecPour = pour ? { pour } : {}
  const liste = useQuery(requetes.taches({ vue, ...avecPour }))
  const semaine = useQuery(requetes.taches({ vue: 'ma_semaine', ...avecPour }))
  const tableau = useQuery(requetes.taches({ vue: 'tableau', ...avecPour }))
  const attente = useQuery(requetes.taches({ vue: 'en_attente_tiers', ...avecPour }))
  const reportees = useQuery(requetes.taches({ vue: 'reportees', ...avecPour }))
  const pourAffiche = pour ?? (vue === 'ma_semaine' ? (moi?.id ?? '') : 'tous')
  const autres = (personnes ?? []).filter((p) => p.type === 'humain' && p.id !== moi?.id)
  const comptes: Partial<Record<VueTaches, number | undefined>> = {
    ma_semaine: semaine.data?.length,
    tableau: tableau.data?.filter((t) => t.statut !== 'faite' && t.statut !== 'annulee').length,
    en_attente_tiers: attente.data?.length,
    reportees: reportees.data?.length,
  }
  const courante = VUES.find((v) => v.vue === vue)!
  const [grouper, setGrouper] = useRegroupement('taches')
  const arbre = useMemo(
    () => (grouper && liste.data && vue !== 'tableau' ? arbreTaches(liste.data) : null),
    [grouper, liste.data, vue],
  )
  // Ordre de parcours une à une depuis la fiche : celui de l'écran (au tableau, par colonne ;
  // regroupée, groupe après groupe).
  useEffect(() => {
    if (!liste.data) return
    retenirFile(
      `taches:${vue}`,
      vue === 'tableau' ? parColonne(liste.data) : arbre ? elementsDansLOrdre(arbre) : liste.data,
    )
  }, [liste.data, vue, arbre])

  return (
    <main>
      <EntetePage
        surtitre="Suivi"
        titre="Tâches"
        action={{ icone: 'add', libelle: 'Nouvelle tâche', onClick: () => setAjout(true) }}
      />
      <div className="pile">
        <div className="barre-outils">
          <Onglets
            valeur={vue}
            onChange={onChangeVue}
            defilants
            onglets={VUES.map((v) => ({
              valeur: v.vue,
              libelle: v.libelle,
              icone: ICONES_VUE[v.vue],
              compte: comptes[v.vue],
            }))}
          />
          <label className="filtre-pour">
            <span className="discret">Pour</span>
            <select
              value={pourAffiche}
              onChange={(e) => onChangePour(e.target.value)}
              aria-label="Tâches de qui"
            >
              {moi && <option value={moi.id}>Moi</option>}
              {autres.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
              <option value="tous">Tout le monde</option>
            </select>
          </label>
          {vue !== 'tableau' && <BasculeRegroupement actif={grouper} onChange={setGrouper} />}
          <p className="discret">{courante.aide}</p>
        </div>
        {ajout && (
          <section className="carte">
            <h2>Nouvelle tâche</h2>
            <FormulaireTache fermer={() => setAjout(false)} />
          </section>
        )}
        {liste.isPending && <Vide>Chargement…</Vide>}
        {liste.data?.length === 0 && (
          <section className="carte">
            <Vide>Rien ici.</Vide>
          </section>
        )}
        {liste.data &&
          liste.data.length > 0 &&
          (vue === 'tableau' ? (
            <Tableau taches={liste.data} />
          ) : (
            <section className="carte carte-flush">
              {arbre ? (
                <ListeGroupee
                  noeuds={arbre}
                  cle={(t) => t.id}
                  ligne={(t) => <LigneTache t={t} vue={vue} />}
                  marque={MARQUE_NOUVELLES}
                />
              ) : (
                liste.data.map((t) => <LigneTache key={t.id} t={t} vue={vue} />)
              )}
            </section>
          ))}
      </div>
    </main>
  )
}
